import { useMemo } from 'react';

import { useWorkspaceAgentMentionSearchQueries } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionSearchResult';
import { type Skill, usePrivateSkillsList } from '~/hooks/queries/skills/usePrivateSkillsList';

export type UsePrivateWorkspaceAgentActionsMenuSkillsSearchParams = {
  enabled: boolean;
  query: string;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentActionsMenuSkillsSearchResult = {
  isLoading: boolean;
  skills: readonly Skill[];
};

/**
 * Debounced `Skills.list` with `query` for blended actions-menu search (tasks + skills) and the
 * composer `/` menu.
 * The Saved skills catalog sub-view reads from `PrivateWorkspaceAgentSkillsCatalogProvider`, not this hook.
 */
export const usePrivateWorkspaceAgentActionsMenuSkillsSearch = ({
  enabled,
  query,
  workspaceId,
}: UsePrivateWorkspaceAgentActionsMenuSkillsSearchParams): PrivateWorkspaceAgentActionsMenuSkillsSearchResult => {
  const queries = useWorkspaceAgentMentionSearchQueries({ enabled, query });
  const { debouncedQuery } = queries;

  const list = usePrivateSkillsList(
    {
      omitSkillListType: true,
      query: debouncedQuery,
      workspaceId,
    },
    { enabled: enabled && !!workspaceId && debouncedQuery.length > 0 },
  );

  const skills = useMemo(() => list.data?.pages.flatMap((page) => page.data) ?? [], [list.data]);

  const isDebouncePending = queries.query !== queries.debouncedQuery;
  const isLoading = isDebouncePending || list.isLoading || list.isFetching;

  return useMemo(
    (): PrivateWorkspaceAgentActionsMenuSkillsSearchResult => ({
      isLoading,
      skills,
    }),
    [isLoading, skills],
  );
};
